/**
 * SaveManager.ts - Save/Load functionality for DEFCON TERMINAL
 *
 * Handles saving game state to localStorage and syncing to server.
 * Supports auto-save, version migrations, and state recovery.
 */

import type { GameState, Decision } from '@shared/types';
import type { ScenarioState } from '@shared/types';
import { EventEmitter, type EventMap } from './EventEmitter';
import { TIMING, GAME_VERSION } from '@shared/constants';

/**
 * Save data structure
 */
export interface SaveData {
  version: string;
  timestamp: number;
  gameState: GameState;
  scenarioState: ScenarioState | null;
  decisionHistory: {
    nodeId: string;
    choiceId: string;
    timestamp: number;
    stateSnapshot: ScenarioState;
  }[];
  metadata: {
    handle?: string;
    operatorId?: string;
    totalPlayTime: number;
  };
}

/**
 * Save result
 */
export interface SaveResult {
  success: boolean;
  savedAt: number;
  savedTo: ('local' | 'server')[];
  error?: string;
}

/**
 * Load result
 */
export interface LoadResult {
  success: boolean;
  data?: SaveData;
  source?: 'local' | 'server';
  error?: string;
}

/**
 * Events emitted by SaveManager
 */
export interface SaveManagerEvents extends EventMap {
  'save:start': { target: 'local' | 'server' | 'both' };
  'save:complete': SaveResult;
  'save:error': { error: string; target: 'local' | 'server' };
  'load:start': { source: 'local' | 'server' | 'auto' };
  'load:complete': LoadResult;
  'load:error': { error: string; source: 'local' | 'server' };
  'autosave:tick': { nextSaveIn: number };
  'migration:applied': { fromVersion: string; toVersion: string };
}

/**
 * Storage keys
 */
const STORAGE_KEYS = {
  saveData: 'defcon_terminal_save',
  quickSave: 'defcon_terminal_quicksave',
  preferences: 'defcon_terminal_prefs',
} as const;

/**
 * API configuration
 */
export interface ApiConfig {
  baseUrl: string;
  sessionToken?: string;
  operatorId?: string;
}

/**
 * SaveManager class - Manages game saves
 */
export class SaveManager extends EventEmitter<SaveManagerEvents> {
  private apiConfig: ApiConfig | null = null;
  private autoSaveInterval: ReturnType<typeof setInterval> | null = null;
  private lastSaveTime: number = 0;
  private isDirty: boolean = false;
  private currentSaveData: SaveData | null = null;

  constructor(apiConfig?: ApiConfig) {
    super();
    this.apiConfig = apiConfig ?? null;
  }

  /**
   * Configure API settings
   */
  setApiConfig(config: ApiConfig): void {
    this.apiConfig = config;
  }

  /**
   * Set session token for API calls
   */
  setSessionToken(token: string): void {
    if (this.apiConfig) {
      this.apiConfig.sessionToken = token;
    }
  }

  /**
   * Mark save data as dirty (needs saving)
   */
  markDirty(): void {
    this.isDirty = true;
  }

  /**
   * Check if save data is dirty
   */
  checkDirty(): boolean {
    return this.isDirty;
  }

  /**
   * Create a save data object
   */
  createSaveData(
    gameState: GameState,
    scenarioState: ScenarioState | null,
    decisionHistory: {
      nodeId: string;
      choiceId: string;
      timestamp: number;
      stateSnapshot: ScenarioState;
    }[],
    metadata?: { handle?: string; operatorId?: string }
  ): SaveData {
    return {
      version: GAME_VERSION,
      timestamp: Date.now(),
      gameState,
      scenarioState,
      decisionHistory,
      metadata: {
        ...metadata,
        totalPlayTime: gameState.totalPlayTime,
      },
    };
  }

  /**
   * Save game state to localStorage
   */
  async saveToLocal(data: SaveData): Promise<SaveResult> {
    this.emit('save:start', { target: 'local' });

    try {
      const serialized = JSON.stringify(data);
      localStorage.setItem(STORAGE_KEYS.saveData, serialized);

      this.lastSaveTime = Date.now();
      this.isDirty = false;
      this.currentSaveData = data;

      const result: SaveResult = {
        success: true,
        savedAt: this.lastSaveTime,
        savedTo: ['local'],
      };

      // Event emission handled by the outer save() method to avoid double-firing
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      this.emit('save:error', { error: errorMessage, target: 'local' });

      return {
        success: false,
        savedAt: 0,
        savedTo: [],
        error: errorMessage,
      };
    }
  }

  /**
   * Save game state to server via API
   */
  async saveToServer(data: SaveData): Promise<SaveResult> {
    if (!this.apiConfig?.sessionToken) {
      return {
        success: false,
        savedAt: 0,
        savedTo: [],
        error: 'No session token configured',
      };
    }

    this.emit('save:start', { target: 'server' });

    try {
      const response = await fetch(`${this.apiConfig.baseUrl}/api/game/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiConfig.sessionToken}`,
        },
        body: JSON.stringify({
          sessionToken: this.apiConfig.sessionToken,
          gameState: data.gameState,
          scenarioState: data.scenarioState,
          decisionHistory: data.decisionHistory,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const result = await response.json() as { data?: { savedAt?: number } };
      const savedAt = result?.data?.savedAt ?? Date.now();

      this.lastSaveTime = savedAt;
      this.isDirty = false;

      const saveResult: SaveResult = {
        success: true,
        savedAt: this.lastSaveTime,
        savedTo: ['server'],
      };

      this.emit('save:complete', saveResult);
      return saveResult;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      this.emit('save:error', { error: errorMessage, target: 'server' });

      return {
        success: false,
        savedAt: 0,
        savedTo: [],
        error: errorMessage,
      };
    }
  }

  /**
   * Save to both local and server
   */
  async save(data: SaveData): Promise<SaveResult> {
    this.emit('save:start', { target: 'both' });

    const savedTo: ('local' | 'server')[] = [];
    let lastError: string | undefined;

    // Always save to local first
    const localResult = await this.saveToLocal(data);
    if (localResult.success) {
      savedTo.push('local');
    } else {
      lastError = localResult.error;
    }

    // Try to save to server if configured
    if (this.apiConfig?.sessionToken) {
      const serverResult = await this.saveToServer(data);
      if (serverResult.success) {
        savedTo.push('server');
      } else if (!lastError) {
        lastError = serverResult.error;
      }
    }

    const result: SaveResult = {
      success: savedTo.length > 0,
      savedAt: this.lastSaveTime,
      savedTo,
      error: savedTo.length === 0 ? lastError : undefined,
    };

    if (result.success) {
      this.emit('save:complete', result);
    }

    return result;
  }

  /**
   * Quick save (local only, minimal data)
   */
  async quickSave(
    currentNodeId: string,
    scenarioId: string,
    scenarioState: ScenarioState
  ): Promise<boolean> {
    try {
      const quickData = {
        version: GAME_VERSION,
        timestamp: Date.now(),
        currentNodeId,
        scenarioId,
        scenarioState,
      };

      localStorage.setItem(STORAGE_KEYS.quickSave, JSON.stringify(quickData));
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Load quick save
   */
  loadQuickSave(): {
    currentNodeId: string;
    scenarioId: string;
    scenarioState: ScenarioState;
    timestamp: number;
  } | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.quickSave);
      if (!stored) {
        return null;
      }

      const data = JSON.parse(stored);

      // Validate quick save is not too old (1 hour)
      const maxAge = 60 * 60 * 1000;
      if (Date.now() - data.timestamp > maxAge) {
        this.clearQuickSave();
        return null;
      }

      return data;
    } catch {
      return null;
    }
  }

  /**
   * Clear quick save
   */
  clearQuickSave(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.quickSave);
    } catch {
      // Ignore errors
    }
  }

  /**
   * Load game state from localStorage
   */
  async loadFromLocal(): Promise<LoadResult> {
    this.emit('load:start', { source: 'local' });

    try {
      const stored = localStorage.getItem(STORAGE_KEYS.saveData);
      if (!stored) {
        return {
          success: false,
          error: 'No local save found',
        };
      }

      const data = JSON.parse(stored) as SaveData;

      // Check version and migrate if needed
      const migratedData = this.migrateIfNeeded(data);

      this.currentSaveData = migratedData;

      const result: LoadResult = {
        success: true,
        data: migratedData,
        source: 'local',
      };

      this.emit('load:complete', result);
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      this.emit('load:error', { error: errorMessage, source: 'local' });

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Load game state from server
   */
  async loadFromServer(): Promise<LoadResult> {
    if (!this.apiConfig?.operatorId) {
      return {
        success: false,
        error: 'No operator ID configured',
      };
    }

    this.emit('load:start', { source: 'server' });

    try {
      const response = await fetch(`${this.apiConfig.baseUrl}/api/player/load`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ operatorId: this.apiConfig.operatorId }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: false,
            error: 'No server save found',
          };
        }
        throw new Error(`Server responded with ${response.status}`);
      }

      interface ServerSaveData {
        version?: string;
        savedAt?: number;
        gameState?: GameState;
        scenarioState?: ScenarioState;
        decisionHistory?: SaveData['decisionHistory'];
        handle?: string;
        operatorId?: string;
      }

      const serverData = await response.json() as { success?: boolean; error?: string; data?: ServerSaveData };
      if (!serverData?.success) {
        throw new Error(serverData?.error || 'Server load failed');
      }

      const payload = serverData.data || {} as ServerSaveData;

      // Convert server response to SaveData format
      const defaultGameState: GameState = {
        currentPath: null,
        currentScenario: null,
        currentNode: null,
        decisions: [],
        completedScenarios: [],
        completedScenarioDetails: [],
        discoveredEasterEggs: [],
        totalPlayTime: 0,
        scenarioStartTime: null,
      };

      const data: SaveData = {
        version: payload.version || GAME_VERSION,
        timestamp: payload.savedAt || Date.now(),
        gameState: payload.gameState || defaultGameState,
        scenarioState: payload.scenarioState || null,
        decisionHistory: payload.decisionHistory || [],
        metadata: {
          handle: payload.handle,
          operatorId: payload.operatorId,
          totalPlayTime: payload.gameState?.totalPlayTime || 0,
        },
      };

      // Migrate if needed
      const migratedData = this.migrateIfNeeded(data);

      this.currentSaveData = migratedData;

      const result: LoadResult = {
        success: true,
        data: migratedData,
        source: 'server',
      };

      this.emit('load:complete', result);
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      this.emit('load:error', { error: errorMessage, source: 'server' });

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Load from best available source (prefers server, falls back to local)
   */
  async load(): Promise<LoadResult> {
    this.emit('load:start', { source: 'auto' });

    // Try server first if configured
    if (this.apiConfig?.sessionToken) {
      const serverResult = await this.loadFromServer();
      if (serverResult.success) {
        return serverResult;
      }
    }

    // Fall back to local
    const localResult = await this.loadFromLocal();
    if (localResult.success) {
      return localResult;
    }

    return {
      success: false,
      error: 'No save data found',
    };
  }

  /**
   * Check if a save exists
   */
  hasSave(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEYS.saveData) !== null;
    } catch {
      return false;
    }
  }

  /**
   * Get current save data without loading
   */
  getCurrentSaveData(): SaveData | null {
    return this.currentSaveData;
  }

  /**
   * Get last save time
   */
  getLastSaveTime(): number {
    return this.lastSaveTime;
  }

  /**
   * Start auto-save interval
   */
  startAutoSave(
    getSaveData: () => SaveData,
    interval: number = TIMING.saveInterval
  ): void {
    this.stopAutoSave();

    this.autoSaveInterval = setInterval(async () => {
      if (this.isDirty) {
        const data = getSaveData();
        await this.saveToLocal(data);
      }

      const nextSaveIn = interval - (Date.now() - this.lastSaveTime);
      this.emit('autosave:tick', { nextSaveIn: Math.max(0, nextSaveIn) });
    }, interval);
  }

  /**
   * Stop auto-save interval
   */
  stopAutoSave(): void {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    }
  }

  /**
   * Migrate save data if version mismatch
   */
  private migrateIfNeeded(data: SaveData): SaveData {
    if (data.version === GAME_VERSION) {
      return data;
    }

    // Apply migrations based on version
    let migratedData = { ...data };

    // Example migration from older versions
    if (this.compareVersions(data.version, '1.0.0') < 0) {
      migratedData = this.migrateToV1(migratedData);
    }

    // Update version after migrations
    migratedData.version = GAME_VERSION;

    this.emit('migration:applied', {
      fromVersion: data.version,
      toVersion: GAME_VERSION,
    });

    return migratedData;
  }

  /**
   * Compare version strings
   */
  private compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const p1 = parts1[i] || 0;
      const p2 = parts2[i] || 0;

      if (p1 < p2) return -1;
      if (p1 > p2) return 1;
    }

    return 0;
  }

  /**
   * Migration to v1.0.0
   */
  private migrateToV1(data: SaveData): SaveData {
    // Ensure all required fields exist
    return {
      ...data,
      gameState: {
        currentPath: data.gameState?.currentPath ?? null,
        currentScenario: data.gameState?.currentScenario ?? null,
        currentNode: data.gameState?.currentNode ?? null,
        decisions: data.gameState?.decisions ?? [],
        completedScenarios: data.gameState?.completedScenarios ?? [],
        completedScenarioDetails: data.gameState?.completedScenarioDetails ?? [],
        discoveredEasterEggs: data.gameState?.discoveredEasterEggs ?? [],
        totalPlayTime: data.gameState?.totalPlayTime ?? 0,
        scenarioStartTime: data.gameState?.scenarioStartTime ?? null,
      },
      scenarioState: data.scenarioState ?? null,
      decisionHistory: data.decisionHistory ?? [],
      metadata: {
        ...data.metadata,
        totalPlayTime: data.metadata?.totalPlayTime ?? 0,
      },
    };
  }

  /**
   * Delete all save data
   */
  async deleteSave(): Promise<boolean> {
    try {
      localStorage.removeItem(STORAGE_KEYS.saveData);
      localStorage.removeItem(STORAGE_KEYS.quickSave);
      this.currentSaveData = null;
      this.isDirty = false;

      // Server deletion is not supported by the current API.

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Export save data as JSON string
   */
  exportSave(): string | null {
    if (!this.currentSaveData) {
      return null;
    }
    return JSON.stringify(this.currentSaveData, null, 2);
  }

  /**
   * Import save data from JSON string
   */
  importSave(json: string): SaveData | null {
    try {
      const data = JSON.parse(json) as SaveData;

      // Validate basic structure
      if (!data.version || !data.gameState) {
        return null;
      }

      // Migrate if needed
      const migratedData = this.migrateIfNeeded(data);
      this.currentSaveData = migratedData;

      return migratedData;
    } catch {
      return null;
    }
  }
}
